import { sanitizeOrganizedTourQuoteText } from "./sanitizeOrganizedTourQuoteText";
import { pickOrganizedTourHeroImageUrl } from "./pickOrganizedTourHeroImageUrl";
import { snapshotOrganizedTourRoomsForPublicOffer } from "./snapshotOrganizedTourRoomsForPublicOffer";
import { sumOrganizedTourPublicOfferRoomsIls } from "./resolveOrganizedTourPublicOfferFinalPriceIls";
import { resolveOrganizedTourDualPrice } from "./resolveOrganizedTourDualPrice";

/** העתקה JSON בטוחה לשמירה ב-public_view_model (פרטי טיסה ללקוח) */
function snapshotLinkedFlightsForPublicOffer(linkedFlights) {
  if (!linkedFlights || typeof linkedFlights !== "object") return null;
  try {
    const copy = JSON.parse(JSON.stringify(linkedFlights));
    const flight = copy?.flight;
    if (flight == null) return null;
    if (Array.isArray(flight) && flight.length === 0) return null;
    return copy;
  } catch {
    return null;
  }
}

/**
 * עמלה באחוז או בסכום קבוע גבוהה ממחיר החדר. כותבים אותה לשורות החדר
 * כדי שעמוד הלקוח, שמסכם חדרים, יציג את אותו סה״כ (₪12,801 + 10% = ₪14,080).
 */
export function liftOrganizedTourRoomPricesToQuote(rooms, quoteFinalIls) {
  if (!Array.isArray(rooms) || !(quoteFinalIls > 0)) return rooms;
  const indexes = [];
  rooms.forEach((row, i) => {
    const n = Number(row?.price_ils);
    if (Number.isFinite(n) && n > 0) indexes.push(i);
  });
  if (!indexes.length) return rooms;
  const sum = indexes.reduce((acc, i) => acc + Math.round(Number(rooms[i].price_ils)), 0);
  if (!(quoteFinalIls > sum)) return rooms;
  const factor = quoteFinalIls / sum;
  let allocated = 0;
  const next = rooms.map((row) => ({ ...row }));
  indexes.forEach((i, n) => {
    const raw = Math.round(Number(next[i].price_ils));
    const isLast = n === indexes.length - 1;
    const price = isLast ? quoteFinalIls - allocated : Math.round(raw * factor);
    allocated += price;
    next[i] = { ...next[i], price_ils: price };
  });
  return next;
}

/**
 * צילום הצעה מלא נשמר ב-public_offer_links (אותה טבלה כמו חבילות/השוואות).
 * supplyMeta — לסניטיזציה בלבד; לא מאוחסן בטקסט גולמי לפני החלפות.
 *
 * @param {object} args
 */
export function buildOrganizedTourSmartOfferPayload(args) {
  const {
    selectedResult,
    cardVm,
    supplierMeta,
    itinerarySourceText,
    termsSourceText,
    finalQuotePriceIls,
    customerName,
    customerPhone,
    customerEmail,
    branding,
    dealId,
    additionalInfoSourceText = "",
    roomsAndPaxInfo = null,
  } = args;

  const itinerarySan = sanitizeOrganizedTourQuoteText(itinerarySourceText, supplierMeta);
  const termsSan = sanitizeOrganizedTourQuoteText(termsSourceText, supplierMeta);
  const additionalSan = sanitizeOrganizedTourQuoteText(additionalInfoSourceText, supplierMeta);
  const tourTitleHe = sanitizeOrganizedTourQuoteText(
    String(cardVm?.tourName || selectedResult?.tourName || "טיול מאורגן"),
    supplierMeta
  );
  const routeLineHe = sanitizeOrganizedTourQuoteText(
    String(cardVm?.routeLabel || selectedResult?.routeLabel || ""),
    supplierMeta
  );
  const datesLineHe = sanitizeOrganizedTourQuoteText(
    String(cardVm?.dateRangeDisplay || selectedResult?.dateRangeDisplay || ""),
    supplierMeta
  );
  const supplierNameHe = sanitizeOrganizedTourQuoteText(
    String(supplierMeta?.supplierName || selectedResult?.supplierDetails?.supplierName || ""),
    supplierMeta
  );

  const heroPick = pickOrganizedTourHeroImageUrl(cardVm, selectedResult ?? cardVm?.raw);
  /** @type {string|null} */
  const heroImageUrlPersisted =
    heroPick && String(heroPick).trim() ? String(heroPick).trim() : null;

  const linkedFlightsSnapshot = snapshotLinkedFlightsForPublicOffer(selectedResult?.linkedFlights);
  const roomsSnapshot = snapshotOrganizedTourRoomsForPublicOffer({
    roomsAndPaxInfo,
    selectedResult,
  });

  // חדרים אחרי HR מנצחים נטו ספק. עמלה באחוז או בסכום נכתבת גם על שורות החדר.
  const quoteFinalIls = Math.round(Number(finalQuotePriceIls) || 0);
  const roomsForCustomer = liftOrganizedTourRoomPricesToQuote(roomsSnapshot, quoteFinalIls);
  const liftedSum = sumOrganizedTourPublicOfferRoomsIls(roomsForCustomer);
  const finalPriceIls =
    liftedSum != null && liftedSum > 0 && quoteFinalIls > 0
      ? Math.max(liftedSum, quoteFinalIls)
      : liftedSum != null && liftedSum > 0
        ? liftedSum
        : quoteFinalIls;

  const dual = resolveOrganizedTourDualPrice(selectedResult, finalPriceIls);
  const primaryCurrency =
    dual?.currency && String(dual.currency).toUpperCase() !== "ILS"
      ? String(dual.currency).toUpperCase()
      : null;
  const fxRate =
    dual?.exchangeRate != null && Number(dual.exchangeRate) > 0
      ? Number(dual.exchangeRate)
      : null;
  const finalPrimary =
    dual?.amount != null && Number(dual.amount) > 0 ? Number(dual.amount) : null;

  const roomsWithFx = Array.isArray(roomsForCustomer)
    ? roomsForCustomer.map((row) => {
        const roomIls = Number(row?.price_ils);
        if (!Number.isFinite(roomIls) || roomIls <= 0 || !primaryCurrency) return row;
        let pricePrimary = null;
        if (fxRate != null && fxRate > 0) {
          pricePrimary = roomIls / fxRate;
        } else if (finalPrimary != null && finalPriceIls > 0) {
          pricePrimary = (roomIls / finalPriceIls) * finalPrimary;
        }
        if (pricePrimary == null || !(pricePrimary > 0)) return row;
        return {
          ...row,
          currency: primaryCurrency,
          price_primary: Math.round(pricePrimary * 100) / 100,
        };
      })
    : roomsSnapshot;

  const pub = {
    version: 2,
    offer_type: "organized_tour",
    tour_title_he: tourTitleHe,
    hero_image_url: heroImageUrlPersisted,
    dates_line_he: datesLineHe,
    route_line_he: routeLineHe,
    itinerary_text_he: itinerarySan,
    terms_text_he: termsSan,
    final_price_ils: finalPriceIls,
    ...(primaryCurrency ? { primary_currency: primaryCurrency } : {}),
    ...(fxRate != null ? { fx_rate_to_ils: fxRate } : {}),
    ...(finalPrimary != null ? { final_price_primary: Math.round(finalPrimary * 100) / 100 } : {}),
    customer_name: customerName || null,
    ...(supplierNameHe ? { supplier_name_he: supplierNameHe } : {}),
    branding: branding && typeof branding === "object" ? branding : null,
    ...(linkedFlightsSnapshot ? { linked_flights: linkedFlightsSnapshot } : {}),
    ...(roomsWithFx?.length ? { rooms: roomsWithFx } : {}),
    ...(additionalSan
      ? {
          additional_info_html: additionalSan,
        }
      : {}),
  };

  const payload = {
    snapshot_version: 2,
    offer_type: "organized_tour",
    deal_id: dealId != null ? String(dealId) : null,
    customer: {
      name: String(customerName || "").trim(),
      phone: String(customerPhone || "").trim(),
      email: customerEmail ? String(customerEmail).trim() : null,
    },
    branding: pub.branding,
    public_view_model: pub,
  };

  console.log("[ORG_TOUR_QUOTE_DATA_ADAPTER_TRACE]", {
    keys: Object.keys(payload),
    final_price_ils: pub.final_price_ils,
    tour_title_len: tourTitleHe.length,
    rooms_count: Array.isArray(pub.rooms) ? pub.rooms.length : 0,
  });

  return payload;
}
